import React, { useState, useEffect } from 'react';
import {
    Home, User, LayoutDashboard, Calendar, Briefcase, ChevronDown,
    FolderOpen, History, LogOut, Keyboard, CalendarDays, Layers,
    Monitor, Settings, Menu, X, IndianRupee, Building2, ShieldCheck,
    ChevronRight, Sparkles, Cpu
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import ProfileAvatar from '../component/ProfileAvtar';
import ConfirmationPopup from '../component/ConfirmationPopup';
import axiosInstance from '../networking/axiosconfig';

const Navbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [userName, setUserName] = useState('');
    const [userRole, setUserRole] = useState('');
    const [userRoleNumber, setUserRoleNumber] = useState(null);
    const [userImageURL, setUserImageURL] = useState(null);
    const [error, setError] = useState("");
    const [openLogoutPopup, setOpenLogoutPopup] = useState(false);

    useEffect(() => {
        // Get user data from localStorage
        const firstName = localStorage.getItem("first_name") || '';
        const lastName = localStorage.getItem("last_name") || '';
        const role = localStorage.getItem("role");
        const profileImg = localStorage.getItem("user_profile_img") || '';
        const userId = localStorage.getItem("userId");

        // Combine first and last name
        const fullName = `${firstName} ${lastName}`.trim();
        setUserName(fullName);
        setUserImageURL(profileImg);

        // Store role number for access control
        const roleNum = role !== null && role !== undefined && role !== '' ? Number(role) : null;
        setUserRoleNumber(roleNum);

        // Map role number to role name
        const roleMap = {
            '0': 'Super Admin',
            '1': 'Owner / Pivot',
            '2': 'Manager',
            '3': 'Employee'
        };
        setUserRole(roleMap[String(role)] || 'Super Admin');

        if (userId) {
            getUserDetail(userId);
        }

        // Listen for profile image updates
        const handleProfileImageUpdate = () => {
            const userId = localStorage.getItem("userId");
            if (userId) {
                getUserDetail(userId);
            }
        };

        window.addEventListener('profileImageUpdated', handleProfileImageUpdate);

        return () => {
            window.removeEventListener('profileImageUpdated', handleProfileImageUpdate);
        };
    }, []);

    const handleLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("userId");
        localStorage.removeItem("first_name");
        localStorage.removeItem("last_name");
        localStorage.removeItem("role");
        localStorage.removeItem("user_profile_img");
        localStorage.removeItem("organizationName");
        localStorage.removeItem("organizationId");
        sessionStorage.clear();
        navigate("/", { replace: true });
    };

    function getUserDetail(userId) {
        axiosInstance
            .get(`/api/users/${userId}`)
            .then((res) => {
                const img = res.data.data.user_profile_img;
                let imageUrl = img;
                if (img && !img.startsWith('http')) {
                    imageUrl = `${img.startsWith('/') ? '' : '/'}${img}`;
                }
                const updatedImg = imageUrl ? `${imageUrl}?t=${Date.now()}` : null;
                setUserImageURL(updatedImg);
                if (imageUrl) {
                    localStorage.setItem("user_profile_img", imageUrl);
                }
            })
            .catch((err) => {
                setError(err.message);
            });
    }

    // Top Bar Quick Items based strictly on user role
    const getNavItems = () => {
        // 1. SaaS Platform Super Admin (Role 0) -> ONLY SaaS platform items!
        if (userRoleNumber === 0) {
            return [
                {
                    id: 'saas-clients',
                    label: 'Client Companies',
                    icon: <Building2 size={18} />,
                    path: '/saas-clients',
                    onClick: () => navigate('/saas-clients'),
                    visible: true
                },
                {
                    id: 'machines',
                    label: 'Machines Console (T700/T800)',
                    icon: <Monitor size={18} />,
                    path: '/t700.html',
                    onClick: () => {
                        const w = window.open('/t700.html', 't700console', 'width=1440,height=900,menubar=no,toolbar=no');
                        if (w) w.focus(); else window.location.href = '/t700.html';
                    },
                    visible: true
                }
            ];
        }

        const items = [];

        // 2. Tenant Owner/Pivot (Role 1) and Manager (Role 2) -> 4 Core Daily Tabs ONLY!
        // Extra items (Holidays, Leaves, Configuration) are neatly in the Side Drawer!
        if (userRoleNumber === 1 || userRoleNumber === 2) {
            items.push({
                id: 'dashboard',
                label: 'Dashboard',
                icon: <LayoutDashboard size={17} />,
                path: '/dashboard',
                onClick: () => navigate('/dashboard'),
                visible: true
            });
            items.push({
                id: 'attendance-register',
                label: 'Attendance Register',
                icon: <CalendarDays size={17} />,
                path: '/calendar',
                onClick: () => navigate('/calendar'),
                visible: true
            });
            items.push({
                id: 'manage-employees',
                label: 'Manage Employee',
                icon: <User size={17} />,
                path: '/manage-employees',
                onClick: () => navigate('/manage-employees'),
                visible: true
            });
            items.push({
                id: 'salary-calc',
                label: 'Salary & Slips',
                icon: <IndianRupee size={17} />,
                path: '/salary-calculation',
                onClick: () => navigate('/salary-calculation'),
                visible: true
            });
            return items;
        }

        // 3. Employee (Role 3)
        if (userRoleNumber === 3) {
            items.push({
                id: 'my-profile',
                label: 'My Profile',
                icon: <User size={17} />,
                path: '/my-profile',
                onClick: () => navigate('/my-profile'),
                visible: true
            });
            items.push({
                id: 'employee-attendance',
                label: 'My Attendance',
                icon: <Calendar size={17} />,
                path: '/employee-attendance',
                onClick: () => navigate('/employee-attendance'),
                visible: true
            });
            items.push({
                id: 'employee-leave',
                label: 'Leave',
                icon: <Briefcase size={17} />,
                path: '/employee/leave',
                onClick: () => navigate('/employee/leave'),
                visible: true
            });
            items.push({
                id: 'web-checking',
                label: 'Web Checking',
                icon: <User size={17} />,
                path: '/Web-Checking',
                onClick: () => navigate('/Web-Checking'),
                visible: true
            });
            return items;
        }

        return items;
    };

    const navItems = getNavItems();

    return (
        <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40 w-full overflow-hidden">
            <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16 gap-3">
                    {/* Brand / Logo + Side Menu Button */}
                    <div className="flex items-center gap-3 shrink-0">
                        {/* Side Menu Drawer Toggle (Only for Tenant Admin/Manager) */}
                        {(userRoleNumber === 1 || userRoleNumber === 2) && (
                            <button
                                onClick={() => setSidebarOpen(true)}
                                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-slate-100 hover:bg-blue-50 text-slate-700 hover:text-blue-600 font-bold text-xs transition border border-slate-200 shadow-sm"
                                title="Open All Menus (Leaves, Holidays, Configuration)"
                            >
                                <Menu size={16} className="text-slate-600" />
                                <span className="hidden sm:inline">All Menus</span>
                            </button>
                        )}

                        <img
                            src='/uploads/1758720302618-312666426.png'
                            alt='AroGeek Attendance'
                            className='w-28 h-9 sm:w-32 sm:h-10 object-contain cursor-pointer'
                            onClick={() => {
                                if (userRoleNumber === 0) navigate('/saas-clients');
                                else if (userRoleNumber === 3) navigate('/employee-attendance');
                                else navigate('/dashboard');
                            }}
                        />
                        <div className="hidden md:block pl-2.5 border-l border-slate-200">
                            <span className="text-xs font-bold text-slate-800 uppercase tracking-wider block truncate max-w-[150px]">
                                {userRoleNumber === 0 ? 'SaaS Super Admin' : (localStorage.getItem("organizationName") || 'AroGeek Biometrics')}
                            </span>
                            <button
                                onClick={() => navigate(userRoleNumber === 0 ? '/saas-clients' : '/biometric-settings')}
                                className="text-[10px] text-emerald-600 hover:text-emerald-700 font-semibold flex items-center gap-1 hover:underline transition cursor-pointer"
                                title="Open Biometric Machine Cloud Settings"
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                {userRoleNumber === 0 ? 'Platform Core Active' : 'T700 & T800 Live • Settings'}
                            </button>
                        </div>
                    </div>

                    {/* Desktop Navigation (Quick Core Items - Zero Horizontal Scroll) */}
                    <div className="hidden lg:flex items-center gap-1.5 flex-1 justify-center max-w-2xl">
                        {navItems.map((item) => {
                            const isActive = location.pathname === item.path;
                            return item.visible && (
                                <button
                                    key={item.id}
                                    onClick={item.onClick}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                                        isActive
                                            ? 'bg-blue-600 text-white shadow-sm'
                                            : 'text-slate-700 hover:text-blue-600 hover:bg-slate-100'
                                    }`}
                                    title={item.label}
                                >
                                    {item.icon}
                                    <span>{item.label}</span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Right Side: User Info & Logout */}
                    <div className="hidden md:flex items-center gap-2.5 shrink-0">
                        <div className="flex items-center gap-2 px-2 py-1">
                            <ProfileAvatar
                                url={userImageURL}
                                name={userName}
                                className="h-8 w-8 text-xs font-bold"
                                key={userImageURL}
                            />
                            <div className="flex flex-col text-left">
                                <span className="text-xs font-bold text-gray-800 leading-tight max-w-[120px] truncate">{userName || 'User'}</span>
                                <span className="text-[10px] text-gray-400">{userRole}</span>
                            </div>
                        </div>

                        {/* Logout Button */}
                        <button
                            onClick={() => setOpenLogoutPopup(true)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl hover:bg-red-50 text-slate-600 hover:text-red-600 border border-slate-200 hover:border-red-200 text-xs font-semibold transition shadow-sm"
                            title="Logout"
                        >
                            <LogOut size={14} />
                            <span>Logout</span>
                        </button>
                    </div>

                    {/* Mobile / Tablet menu button */}
                    <div className="lg:hidden flex items-center gap-2">
                        <button
                            onClick={() => setOpenLogoutPopup(true)}
                            className="p-1.5 rounded-xl text-slate-500 hover:text-red-600 hover:bg-red-50 transition"
                            title="Logout"
                        >
                            <LogOut size={18} />
                        </button>
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="p-1.5 rounded-xl text-slate-600 hover:bg-slate-100 transition"
                        >
                            <Menu size={22} />
                        </button>
                    </div>
                </div>
            </div>

            {/* SIDE NAVIGATION DRAWER */}
            {sidebarOpen && (
                <div className="fixed inset-0 z-50 flex">
                    {/* Dark Glass Backdrop */}
                    <div
                        className="fixed inset-0 bg-slate-950/50 backdrop-blur-sm transition-opacity"
                        onClick={() => setSidebarOpen(false)}
                    />

                    {/* Drawer Content Panel */}
                    <aside className="relative w-80 max-w-[85vw] bg-white h-full shadow-2xl flex flex-col z-50 overflow-y-auto animate-fadeIn">
                        {/* Drawer Header */}
                        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                            <div>
                                <h3 className="font-bold text-slate-900 text-sm leading-tight truncate max-w-[200px]">
                                    {localStorage.getItem("organizationName") || 'AroGeek Platform'}
                                </h3>
                                <p className="text-[11px] text-slate-500 mt-0.5">
                                    Navigation & Configuration
                                </p>
                            </div>
                            <button
                                onClick={() => setSidebarOpen(false)}
                                className="p-1.5 rounded-xl text-slate-400 hover:text-slate-800 hover:bg-slate-200 transition"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Navigation Categories */}
                        <div className="p-3.5 space-y-5 flex-1">
                            {/* Category 1: Core Operations */}
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 px-2.5">
                                    Core Operations
                                </p>
                                <div className="space-y-0.5">
                                    <button
                                        onClick={() => { navigate('/dashboard'); setSidebarOpen(false); }}
                                        className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-semibold transition ${
                                            location.pathname === '/dashboard' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <LayoutDashboard size={16} />
                                            <span>Dashboard & Analytics</span>
                                        </div>
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold">
                                            Graphs
                                        </span>
                                    </button>
                                    <button
                                        onClick={() => { navigate('/calendar'); setSidebarOpen(false); }}
                                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition ${
                                            location.pathname === '/calendar' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'
                                        }`}
                                    >
                                        <CalendarDays size={16} />
                                        <span>Attendance Register</span>
                                    </button>
                                    <button
                                        onClick={() => { navigate('/manage-employees'); setSidebarOpen(false); }}
                                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition ${
                                            location.pathname === '/manage-employees' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'
                                        }`}
                                    >
                                        <User size={16} />
                                        <span>Manage Employees</span>
                                    </button>
                                    <button
                                        onClick={() => { navigate('/salary-calculation'); setSidebarOpen(false); }}
                                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition ${
                                            location.pathname === '/salary-calculation' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'
                                        }`}
                                    >
                                        <IndianRupee size={16} />
                                        <span>Salary & Slips</span>
                                    </button>
                                </div>
                            </div>

                            {/* Category 2: Leaves & Holidays */}
                            {(userRoleNumber === 1 || userRoleNumber === 2) && (
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 px-2.5">
                                        Leaves & Attendance
                                    </p>
                                    <div className="space-y-0.5">
                                        <button
                                            onClick={() => { navigate('/holiday-management'); setSidebarOpen(false); }}
                                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition ${
                                                location.pathname === '/holiday-management' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'
                                            }`}
                                        >
                                            <Calendar size={16} />
                                            <span>Holiday Calendar</span>
                                        </button>
                                        <button
                                            onClick={() => { navigate('/leave/current-leave'); setSidebarOpen(false); }}
                                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition ${
                                                location.pathname === '/leave/current-leave' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'
                                            }`}
                                        >
                                            <FolderOpen size={16} />
                                            <span>Current Leaves</span>
                                        </button>
                                        <button
                                            onClick={() => { navigate('/leave/history-leave'); setSidebarOpen(false); }}
                                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition ${
                                                location.pathname === '/leave/history-leave' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'
                                            }`}
                                        >
                                            <History size={16} />
                                            <span>Leave History</span>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Category 3: Company Setup & Masters */}
                            {(userRoleNumber === 1 || userRoleNumber === 2) && (
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 px-2.5">
                                        Company Setup & Masters
                                    </p>
                                    <div className="space-y-0.5">
                                        <button
                                            onClick={() => { navigate('/master/leave'); setSidebarOpen(false); }}
                                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition ${
                                                location.pathname === '/master/leave' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'
                                            }`}
                                        >
                                            <CalendarDays size={16} />
                                            <span>Leave Types Master</span>
                                        </button>
                                        <button
                                            onClick={() => { navigate('/department'); setSidebarOpen(false); }}
                                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition ${
                                                location.pathname === '/department' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'
                                            }`}
                                        >
                                            <Layers size={16} />
                                            <span>Departments</span>
                                        </button>
                                        <button
                                            onClick={() => { navigate('/designation'); setSidebarOpen(false); }}
                                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition ${
                                                location.pathname === '/designation' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'
                                            }`}
                                        >
                                            <Briefcase size={16} />
                                            <span>Designations</span>
                                        </button>
                                        <button
                                            onClick={() => { navigate('/genralSetting'); setSidebarOpen(false); }}
                                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition ${
                                                location.pathname === '/genralSetting' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'
                                            }`}
                                        >
                                            <Settings size={16} />
                                            <span>General Settings</span>
                                        </button>
                                        <button
                                            onClick={() => { navigate('/biometric-settings'); setSidebarOpen(false); }}
                                            className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-semibold transition ${
                                                location.pathname === '/biometric-settings' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <Monitor size={16} />
                                                <span>Biometric Machine Settings</span>
                                            </div>
                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold">
                                                Live
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Category 4: My Profile */}
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 px-2.5">
                                    Account
                                </p>
                                <button
                                    onClick={() => { navigate('/my-profile'); setSidebarOpen(false); }}
                                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-semibold transition ${
                                        location.pathname === '/my-profile' ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-100'
                                    }`}
                                >
                                    <User size={16} />
                                    <span>My Profile</span>
                                </button>
                            </div>
                        </div>

                        {/* Drawer Footer */}
                        <div className="p-3.5 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <ProfileAvatar url={userImageURL} name={userName} className="h-8 w-8 text-xs font-bold" />
                                <div>
                                    <p className="text-xs font-bold text-slate-800 leading-none truncate max-w-[140px]">{userName || 'User'}</p>
                                    <p className="text-[10px] text-slate-400 mt-0.5">{userRole}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => { setSidebarOpen(false); setOpenLogoutPopup(true); }}
                                className="p-2 text-slate-400 hover:text-red-600 rounded-xl hover:bg-red-50 transition"
                                title="Logout"
                            >
                                <LogOut size={16} />
                            </button>
                        </div>
                    </aside>
                </div>
            )}

            {/* Logout Confirmation Popup */}
            <ConfirmationPopup
                open={openLogoutPopup}
                setOpen={setOpenLogoutPopup}
                title="Confirm Logout"
                message="Are you sure you want to logout?"
                confirmText="Logout"
                onConfirm={() => {
                    handleLogout();
                    setOpenLogoutPopup(false);
                }}
            />
        </nav>
    );
};

export default Navbar;
